import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, authHelpers } from '../lib/supabase';
import { emailService } from '../services/emailService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [staffMember, setStaffMember] = useState(null)
  const [loading, setLoading] = useState(true)

  // ⚠️ PROTECTED FUNCTION - DO NOT MODIFY OR ADD ASYNC OPERATIONS
  // This is a Supabase auth state change listener that must remain synchronous
  const handleAuthStateChange = (event, session) => {
    // SYNC OPERATIONS ONLY - NO ASYNC/AWAIT ALLOWED
    if (session?.user) {
      setUser(session?.user)
      // Fetch user profile after auth state changes
      fetchUserProfile(session?.user?.id)
      fetchStaffProfile(session?.user?.id) // Also check if user is staff
    } else {
      setUser(null)
      setUserProfile(null)
      setStaffMember(null)
    }
    setLoading(false)
  }

  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()

      if (!error && data) {
        setUserProfile(data)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  const fetchStaffProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('staff')?.select(`
          *,
          user_profiles!staff_user_id_fkey(*),
          staff_event_assignments(
            *,
            events(*)
          )
        `)?.eq('user_id', userId)?.single();

      if (!error && data) {
        setStaffMember(data);
      }
    } catch (error) {
      console.error('Error fetching staff profile:', error);
    }
  }
  
  useEffect(() => {
    // Get initial session - Use Promise chain
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
        if (session?.user) {
          setUser(session?.user)
          fetchUserProfile(session?.user?.id)
          fetchStaffProfile(session?.user?.id) // Also check if user is staff
        }
        setLoading(false)
      })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(handleAuthStateChange)

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password
      })
      
      if (error) throw error
      
      return { user: data?.user, error: null }
    } catch (error) {
      console.error('Sign in error:', error)
      return { user: null, error: error?.message || 'Failed to sign in' }
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email, password, userData = {}) => {
    try {
      setLoading(true)
      
      // Use standard Supabase signup for all users, including attendees
      const { data, error } = await supabase?.auth?.signUp({
        email: email?.trim(),
        password,
        options: {
          data: {
            full_name: userData?.full_name || `${userData?.firstName || ''} ${userData?.lastName || ''}`?.trim(),
            first_name: userData?.firstName,
            last_name: userData?.lastName,
            role: userData?.role || 'attendee'
          }
        }
      })
      
      if (error) {
        // Enhanced error handling for email confirmation issues
        if (error?.message?.includes('Error sending confirmation email') || 
            error?.message?.includes('email confirmation') ||
            error?.message?.includes('SMTP') ||
            error?.message?.includes('email delivery')) {
          
          console.warn('Email confirmation failed, but account may have been created:', error?.message)
          
          return { 
            user: null, 
            error: 'Account creation completed, but we encountered an issue sending the confirmation email. Please try signing in with your credentials. If that doesn\'t work, wait a few minutes and try again, or contact support.',
            isEmailError: true,
            canTrySignIn: true
          }
        }
        
        throw error
      }
      
      // Standard flow: If signup succeeds normally
      if (data?.user) {
        // ✅ Enhanced welcome email with better error handling and user feedback
        try {
          console.log('🚀 Attempting to send welcome email to new user:', {
            email: data?.user?.email,
            full_name: userData?.full_name || `${userData?.firstName || ''} ${userData?.lastName || ''}`?.trim() || 'New User'
          });

          await emailService?.sendWelcomeEmail({
            email: data?.user?.email,
            full_name: userData?.full_name || `${userData?.firstName || ''} ${userData?.lastName || ''}`?.trim() || 'New User'
          });
          
          console.log('✅ Welcome email sent successfully via emailService')
        } catch (emailError) {
          console.warn('⚠️ Welcome email could not be sent:', emailError);
          
          // ✅ Enhanced error logging for debugging
          if (emailError?.isEmailServiceError) {
            console.error('📧 Email service specific error:', {
              message: emailError?.userFriendlyMessage || 'Unknown email error',
              originalError: emailError?.originalError?.message,
              timestamp: new Date()?.toISOString(),
              userEmail: data?.user?.email
            });
          } else {
            console.error('📧 Unexpected email error:', {
              message: emailError?.message,
              stack: emailError?.stack,
              timestamp: new Date()?.toISOString(),
              userEmail: data?.user?.email
            });
          }
          
          // ✅ Don't fail signup if email fails - continue with account creation
          console.log('ℹ️ Continuing with account creation despite email send failure');
        }
        
        // Check if user needs email confirmation
        const needsConfirmation = !data?.user?.email_confirmed_at && data?.user?.identities?.length === 0
        
        if (needsConfirmation) {
          return { 
            user: data?.user, 
            error: null,
            needsConfirmation: true,
            message: 'Account created! Please check your email to confirm your account before signing in. (Note: Welcome email may take a few minutes to arrive.)'
          }
        } else {
          return { 
            user: data?.user, 
            error: null,
            message: 'Account created successfully! You can now explore EventGrid. (Welcome email may take a few minutes to arrive.)'
          }
        }
      }
      
      return { user: data?.user, error: null }
    } catch (error) {
      console.error('Sign up error:', error)
      
      // Enhanced error handling with specific messaging
      if (error?.message?.includes('Error sending confirmation email') || 
          error?.message?.includes('SMTP') ||
          error?.message?.includes('email delivery') ||
          error?.message?.includes('email service')) {
        return { 
          user: null, 
          error: 'We encountered an issue with email verification. Your account may have been created successfully - please try signing in first. If that doesn\'t work, contact support for assistance.',
          isEmailError: true,
          canTrySignIn: true
        }
      }
      
      if (error?.message?.includes('User already registered')) {
        return { 
          user: null, 
          error: 'An account with this email already exists. Please sign in instead.',
          isExistingUser: true
        }
      }
      
      if (error?.message?.includes('Invalid email')) {
        return { 
          user: null, 
          error: 'Please enter a valid email address.'
        }
      }
      
      if (error?.message?.includes('Password should be at least')) {
        return { 
          user: null, 
          error: 'Password should be at least 6 characters long.'
        }
      }
      
      return { user: null, error: error?.message || 'Failed to create account. Please try again.' }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (error) throw error
      
      setUser(null)
      setUserProfile(null)
      setStaffMember(null)
      return { error: null }
    } catch (error) {
      console.error('Sign out error:', error)
      return { error: error?.message || 'Failed to sign out' }
    }
  }

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email?.trim(), {
        redirectTo: `${window?.location?.origin}/reset-password`
      })
      
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Password reset error:', error)
      return { error: error?.message || 'Failed to send reset email' }
    }
  }

  const updatePassword = async (password) => {
    try {
      const { error } = await supabase?.auth?.updateUser({ password })
      if (error) throw error
      return { error: null }
    } catch (error) {
      console.error('Password update error:', error)
      return { error: error?.message || 'Failed to update password' }
    }
  }

  const updateProfile = async (updates) => {
    try {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()

      if (error) throw error

      setUserProfile(data)
      return { data, error: null }
    } catch (error) {
      console.error('Profile update error:', error)
      return { data: null, error: error?.message || 'Failed to update profile' }
    }
  }

  // Enhanced role checking with staff member support
  const isAuthenticated = !!user
  const isAdmin = userProfile?.role === 'admin'
  const isHost = userProfile?.role === 'host'
  const isAttendee = userProfile?.role === 'attendee'
  const isStaff = !!staffMember && staffMember?.status === 'active'
  const isCheckInStaff = isStaff && staffMember?.staff_role === 'check_in_staff'

  // Enhanced permission checking methods
  const hasRole = (requiredRole) => userProfile?.role === requiredRole
  const hasAnyRole = (roles) => roles?.includes(userProfile?.role)
  const canCreateEvent = () => authHelpers?.canCreateEvent(userProfile)
  const canManageEvent = (eventHostId) => authHelpers?.canManageEvent(userProfile, eventHostId)
  const canViewEventBookings = (eventHostId) => authHelpers?.canViewEventBookings(userProfile, eventHostId)
  const canManageAllUsers = () => authHelpers?.canManageAllUsers(userProfile)

  // Navigation helpers based on role (enhanced for staff)
  const getDefaultRoute = () => {
    if (isAdmin) return '/admin-dashboard'
    if (isHost) return '/host-dashboard'
    if (isStaff) {
      // Staff members typically go to attendee management for check-ins
      return '/attendee-management'
    }
    if (isAttendee) return '/user-dashboard'
    return '/event-discovery'
  }

  const getAvailableRoutes = () => {
    const baseRoutes = [
      { path: '/', name: 'Events', public: true },
      { path: '/event-discovery', name: 'Discover Events', public: true }
    ]

    if (!isAuthenticated) {
      return [
        ...baseRoutes,
        { path: '/authentication', name: 'Sign In', public: true }
      ]
    }

    const userRoutes = [
      ...baseRoutes,
      { path: '/user-dashboard', name: 'Dashboard', roles: ['attendee'] }
    ]

    const hostRoutes = [
      { path: '/host-dashboard', name: 'Host Dashboard', roles: ['host'] },
      { path: '/event-creation', name: 'Create Event', roles: ['host'] },
      { path: '/event-management', name: 'Manage Events', roles: ['host'] },
      { path: '/attendee-management', name: 'Attendee Management', roles: ['host'] },
      { path: '/staff-management', name: 'Staff Management', roles: ['host'] }
    ]

    const adminRoutes = [
      { path: '/admin-dashboard', name: 'Admin Dashboard', roles: ['admin'] }
    ]

    const staffRoutes = [
      { path: '/attendee-management', name: 'Check-In', roles: ['staff'] }
    ]

    let availableRoutes = [...userRoutes]

    if (isHost || isAdmin) {
      availableRoutes = [...availableRoutes, ...hostRoutes]
    }

    if (isAdmin) {
      availableRoutes = [...availableRoutes, ...adminRoutes]
    }

    if (isStaff) {
      availableRoutes = [...availableRoutes, ...staffRoutes]
    }

    return availableRoutes?.filter(route => 
      route?.public || 
      !route?.roles || 
      route?.roles?.includes(userProfile?.role) ||
      (route?.roles?.includes('staff') && isStaff)
    );
  }

  const value = {
    // User state
    user,
    userProfile,
    staffMember,
    loading,
    
    // Authentication methods
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    
    // Role checking methods
    isAuthenticated,
    isAdmin,
    isHost,
    isAttendee,
    isStaff,
    isCheckInStaff,
    hasRole,
    hasAnyRole,
    
    // Permission checking methods
    canCreateEvent,
    canManageEvent,
    canViewEventBookings,
    canManageAllUsers,
    
    // Navigation helpers
    getDefaultRoute,
    getAvailableRoutes,
    
    // Direct access
    userRole: userProfile?.role,
    staffRole: staffMember?.staff_role
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}